'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { formatTelefoneComCodigoPais } from '@/shared/utils/contato';
import { FeatherAlertCircle, FeatherChevronDown, FeatherChevronUp, FeatherPhone } from '@subframe/core';
import type { NegociacaoCasoTelefoneUi } from '../../types/negociacaoDetailTypes';
import { encontrarNumeroAtivo, listarCanaisAlternativos, resolveTagDoCanalAtivo, resolveTelefoneTag } from '../../utils/resolveTelefoneTag';
import { TelefoneTagBadge } from '../common/TelefoneTagBadge';
interface NegociacaoDetailTelefonesProps {
  /** Telefone do caso — o fallback para negociação legada, que não tem fila. */
  counterpartyPhone: string;
  telefones: NegociacaoCasoTelefoneUi[];
  isLoading: boolean;
  error: string | null;
  onCarregar: () => void;
}

/**
 * O bloco de telefones do painel lateral.
 *
 * Antes desta feature o painel mostrava um número fixo, sem espaço para os outros nem para os
 * status deles. Agora mostra o número ATIVO com a tag `Negociando` e, havendo mais de um, o
 * header `+N canais` que expande a lista **inline** (sem modal, sem drawer).
 *
 * A fila é carregada sob demanda, no primeiro clique: a maioria dos casos tem um número só, e
 * buscar a fila de todo caso aberto seria uma requisição por visita para nada.
 */
export function NegociacaoDetailTelefones(t0) {
  const $ = _c(27);
  const {
    counterpartyPhone,
    telefones,
    isLoading,
    error,
    onCarregar
  } = t0;
  const [expandido, setExpandido] = useState(false);
  let t1;
  if ($[0] !== telefones) {
    t1 = encontrarNumeroAtivo(telefones);
    $[0] = telefones;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const ativo = t1;
  let t2;
  if ($[2] !== ativo?.numeroNormalizado || $[3] !== counterpartyPhone || $[4] !== error || $[5] !== expandido || $[6] !== isLoading || $[7] !== onCarregar || $[8] !== telefones) {
    const alternativos = listarCanaisAlternativos(telefones);
    const numeroExibido = ativo?.numeroNormalizado ?? counterpartyPhone;
    let t3;
    if ($[10] !== telefones) {
      t3 = resolveTagDoCanalAtivo(telefones);
      $[10] = telefones;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    const tagDoAtivo = t3;
    let t4;
    if ($[12] !== expandido || $[13] !== onCarregar || $[14] !== telefones.length) {
      t4 = () => {
        if (!expandido && telefones.length === 0) {
          onCarregar();
        }
        setExpandido(_temp);
      };
      $[12] = expandido;
      $[13] = onCarregar;
      $[14] = telefones.length;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    const alternar = t4;
    let t5;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <span className="text-subtext-color text-xs">Telefones</span>;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <FeatherPhone className="mt-0.5 size-4 shrink-0 text-neutral-400" />;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== numeroExibido) {
      t7 = numeroExibido ? formatTelefoneComCodigoPais(numeroExibido) : "\u2014";
      $[18] = numeroExibido;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== t7) {
      t8 = <span className="text-body-xs-medium text-default-font break-all">{t7}</span>;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== tagDoAtivo) {
      t9 = tagDoAtivo ? <TelefoneTagBadge tag={tagDoAtivo} /> : null;
      $[22] = tagDoAtivo;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] !== t8 || $[25] !== t9) {
      t10 = <div className="flex items-start gap-2">{t6}<div className="flex min-w-0 flex-col items-start gap-1.5">{t8}{t9}</div></div>;
      $[24] = t8;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    t2 = <div className="flex flex-col gap-1">{t5}{t10}{(telefones.length === 0 || alternativos.length > 0) && <button type="button" onClick={alternar} aria-expanded={expandido} className="text-caption font-caption text-subtext-color hover:text-default-font flex w-full items-center justify-between py-1"><span>{telefones.length === 0 ? "Ver outros n\xFAmeros" : `+${alternativos.length} ${alternativos.length === 1 ? "canal" : "canais"}`}</span>{expandido ? <FeatherChevronUp className="h-3 w-3" /> : <FeatherChevronDown className="h-3 w-3" />}</button>}{expandido && <div className="flex flex-col">{isLoading && <span className="text-caption font-caption text-subtext-color py-1.5">Carregando…</span>}{error && <div className="border-error-100 bg-error-50 flex items-start gap-2 rounded-lg border border-solid p-3"><div className="border-error-700 text-error-700 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 border-solid"><FeatherAlertCircle className="h-3 w-3" /></div><span className="text-caption font-caption text-error-800">Não foi possível carregar os outros números. Tente novamente.</span></div>}{!isLoading && !error && alternativos.length === 0 && <span className="text-caption font-caption text-subtext-color py-1.5">Este devedor tem apenas um número cadastrado.</span>}{alternativos.map(_temp2)}</div>}</div>;
    $[2] = ativo?.numeroNormalizado;
    $[3] = counterpartyPhone;
    $[4] = error;
    $[5] = expandido;
    $[6] = isLoading;
    $[7] = onCarregar;
    $[8] = telefones;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function _temp2(telefone) {
  const tag = resolveTelefoneTag(telefone);
  return <div key={telefone.id} className="flex flex-col items-start gap-1 py-1.5 pl-6"><span className="text-body-xs-medium text-default-font break-all">{formatTelefoneComCodigoPais(telefone.numeroNormalizado)}</span>{tag ? <TelefoneTagBadge tag={tag} /> : null}</div>;
}
function _temp(atual) {
  return !atual;
}